import { LogManager } from "../logging/LogManager";
import { ObjectUtil } from "../ObjectUtil";

const log = LogManager.getLogger("core/settings/LocalUserSettings");

export class LocalUserSettings {
    /**
     * The set() and getXXXX() methods are used to persists and retrieve user settings.
     * For now, they are written to local storage.  In the future, we may decide to persist them remotely,
     * but this abstraction gives us the ability to change that (or maybe even make it variable whether
     * this is persisted locally or remotely.)
     * @param key
     * @returns
     */
    public static getString(key: string): string {
        if (key == null) {
            return null;
        }
        return localStorage.getItem(key);
    }

    public static getBoolean(key: string, valueIfNull = false): boolean {
        if (key == null) {
            return valueIfNull;
        }
        const value = localStorage.getItem(key);
        if (value === "true")
            return true;
        else if (value === "false")
            return false;
        return valueIfNull;
    }

    public static getNumber(key: string, valueIfNull = 0): number {
        if (key == null) {
            return valueIfNull;
        }
        const value = localStorage.getItem(key);
        if (value == null)
            return valueIfNull;
        const result = parseInt(value);
        if (isNaN(result))
            return valueIfNull;
        return result;
    }

    public static getObject<T = Record<string, unknown>>(key: string, valueIfNull = null): T {
        if (key == null) {
            return valueIfNull;
        }
        const value = localStorage.getItem(key);
        if (value == null)
            return valueIfNull;
        try {
            return JSON.parse(value) as T;
        } catch (e) {
            log.error(`Failed to parse JSON for key ${key}:`, e);
            return valueIfNull;
        }
    }

    public static hasSetting(key: string, subKey?: string): boolean {
        const result = subKey != null
            ? this.getObject<Record<string, unknown>>(key, null)?.[subKey]
            : this.getObject(key, null);
        return !ObjectUtil.isEmptyObject(result);
    }

    public static getArray<T>(key: string, valueIfNull: T[] = null): T[] {
        if (key == null) {
            return valueIfNull;
        }
        const value = localStorage.getItem(key);
        if (value == null)
            return valueIfNull;
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed as T[] : valueIfNull;
        } catch (e) {
            log.error(`Failed to parse JSON array for key ${key}:`, e);
            return valueIfNull;
        }
    }

    public static set(key: string, value: number | boolean | string | Record<string, unknown> | unknown[]): boolean {
        try {
            if (key == null) {
                return false;
            }

            if (value == null || (typeof value === 'object' && ObjectUtil.isEmptyObject(value))) {
                localStorage.removeItem(key);
            }
            else {
                const stringValue = typeof value === 'object' ? JSON.stringify(value) : value.toString();
                localStorage.setItem(key, stringValue);
            }
        } catch (e) {
            log.error("Failed to save user setting:", e);
            return false;
        }
        return true;
    }
}
